<template>
  <v-container fluid class="profile-page">
    <div class="profile-layout">
      <!-- Left: Avatar Card -->
      <div class="avatar-card">
        <div class="avatar-card__bg"></div>
        <div class="avatar-card__body">
          <div class="avatar-upload" :class="{ 'avatar-upload--uploading': avatarUploading }" @click="triggerUpload()">
            <div class="avatar-upload__circle">
              <img v-if="avatarSrc" :src="avatarSrc" class="avatar-upload__img" referrerpolicy="no-referrer" />
              <span v-else class="avatar-upload__initials">{{ userInitials }}</span>
            </div>
            <div class="avatar-upload__overlay">
              <v-progress-circular
                v-if="avatarUploading"
                indeterminate
                size="14"
                width="2"
                color="white"
              ></v-progress-circular>
              <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="20" height="20">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Z" />
              </svg>
            </div>
            <input
              ref="avatarInput"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              class="avatar-upload__hidden"
              @change="handleAvatarChange"
            />
          </div>
          <h2 class="avatar-card__name">{{ auth.name || 'User' }}</h2>
          <p class="avatar-card__email">{{ auth.email || '' }}</p>
          <div class="avatar-card__roles">
            <span
              v-for="(role, index) in auth.roles"
              :key="index"
              class="role-chip"
            >{{ role.name }}</span>
          </div>
        </div>
      </div>

      <!-- Right: Details -->
      <div class="details-section">
        <!-- Personal Information -->
        <div class="section-card">
          <div class="section-card__header">
            <div class="section-card__icon">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="18" height="18">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
              </svg>
            </div>
            <h3 class="section-card__title">Personal Information</h3>
          </div>
          <div class="section-card__body">
            <div class="info-row">
              <label class="info-row__label">Full Name</label>
              <div class="info-row__field">
                <input
                  type="text"
                  class="info-input info-input--disabled"
                  :value="auth.name || ''"
                  disabled
                />
                <div class="info-row__lock">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="14" height="14">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                  </svg>
                </div>
              </div>
              <span class="info-row__hint">Contact admin to change your name</span>
            </div>
            <div class="info-row">
              <label class="info-row__label">Email Address</label>
              <div class="info-row__field">
                <input
                  type="email"
                  class="info-input info-input--disabled"
                  :value="auth.email || ''"
                  disabled
                />
                <div class="info-row__lock">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="14" height="14">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                  </svg>
                </div>
              </div>
              <span class="info-row__hint">Contact admin to change your email</span>
            </div>
          </div>
        </div>

        <!-- Change Password -->
        <div class="section-card">
          <div class="section-card__header">
            <div class="section-card__icon section-card__icon--amber">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="18" height="18">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" />
              </svg>
            </div>
            <h3 class="section-card__title">Change Password</h3>
          </div>
          <div class="section-card__body">
            <div class="info-row">
              <label class="info-row__label">Current Password</label>
              <div class="info-row__field">
                <input
                  :type="showCurrentPw ? 'text' : 'password'"
                  class="info-input"
                  :class="{ 'info-input--error': pwErrors.currentPassword }"
                  placeholder="Enter current password"
                  v-model="passwordForm.currentPassword"
                />
                <button class="info-row__toggle" @click="showCurrentPw = !showCurrentPw" type="button">
                  <v-icon size="16" color="var(--app-text-faint)">{{ showCurrentPw ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}</v-icon>
                </button>
              </div>
            </div>
            <div class="pw-row">
              <div class="info-row">
                <label class="info-row__label">New Password</label>
                <div class="info-row__field">
                  <input
                    :type="showNewPw ? 'text' : 'password'"
                    class="info-input"
                    :class="{ 'info-input--error': pwErrors.newPassword }"
                    placeholder="Enter new password"
                    v-model="passwordForm.newPassword"
                  />
                  <button class="info-row__toggle" @click="showNewPw = !showNewPw" type="button">
                    <v-icon size="16" color="var(--app-text-faint)">{{ showNewPw ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}</v-icon>
                  </button>
                </div>
              </div>
              <div class="info-row">
                <label class="info-row__label">Confirm New Password</label>
                <div class="info-row__field">
                  <input
                    :type="showConfirmPw ? 'text' : 'password'"
                    class="info-input"
                    :class="{ 'info-input--error': pwErrors.confirmPassword }"
                    placeholder="Re-enter new password"
                    v-model="passwordForm.confirmPassword"
                  />
                  <button class="info-row__toggle" @click="showConfirmPw = !showConfirmPw" type="button">
                    <v-icon size="16" color="var(--app-text-faint)">{{ showConfirmPw ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}</v-icon>
                  </button>
                </div>
              </div>
            </div>
            <div class="section-card__actions">
              <button class="btn btn--outline" @click="resetPasswordForm()">Cancel</button>
              <button class="btn btn--primary" @click="savePassword()">Update Password</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import { mapState } from "vuex";

export default {
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
    userInitials() {
      if (!this.auth.name) return "?";
      return this.auth.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .substring(0, 2)
        .toUpperCase();
    },
    // Local preview takes precedence so the user sees their pick instantly,
    // even before the upload finishes; fall back to the SAS-signed URL the
    // backend stamps on auth.data.
    avatarSrc() {
      return this.avatarPreview || this.auth.avatarUrl || null;
    },
  },
  data: () => ({
    avatarPreview: null,
    avatarFile: null,
    avatarUploading: false,
    showCurrentPw: false,
    showNewPw: false,
    showConfirmPw: false,
    passwordForm: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    pwErrors: {
      currentPassword: false,
      newPassword: false,
      confirmPassword: false,
    },
  }),
  watch: {
    'passwordForm.currentPassword'(val) { if (val) this.pwErrors.currentPassword = false; },
    'passwordForm.newPassword'(val) { if (val) this.pwErrors.newPassword = false; },
    'passwordForm.confirmPassword'(val) { if (val) this.pwErrors.confirmPassword = false; },
  },
  methods: {
    triggerUpload() {
      if (this.avatarUploading) return;
      this.$refs.avatarInput.click();
    },
    handleAvatarChange(e) {
      const file = e.target.files[0];
      // Reset the input so picking the same file again still triggers change.
      e.target.value = "";
      if (!file) return;

      const allowed = ["image/jpeg", "image/png", "image/webp"];
      if (!allowed.includes(file.type)) {
        this.$store.dispatch("showMessage", {
          message: "Unsupported image type. Allowed: JPEG, PNG, WEBP.",
          messageType: "error",
          timeout: 2500,
        });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$store.dispatch("showMessage", {
          message: "Image is too large. Maximum size is 5 MB.",
          messageType: "error",
          timeout: 2500,
        });
        return;
      }

      this.avatarFile = file;
      this.avatarPreview = URL.createObjectURL(file);
      this.uploadAvatar(file);
    },
    uploadAvatar(file) {
      this.avatarUploading = true;
      const fd = new FormData();
      fd.append("avatar", file);

      this.$axios
        .post(`${this.$service.sso}/v1/en/auth/profile/avatar`, fd, {
          headers: {
            Authorization: `Bearer ${this.auth.token}`,
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          const { status, message, data } = response.data;
          if (!status) {
            this.$store.dispatch("showMessage", {
              message: message || "Failed to upload avatar.",
              messageType: "error",
              timeout: 3000,
            });
            this.avatarPreview = null;
            return;
          }

          // Persist the freshly-signed URL onto the auth state so navbar
          // and elsewhere pick it up immediately.
          this.$store.commit("updateAuth", {
            ...this.auth,
            avatarUrl: data && data.avatarUrl ? data.avatarUrl : null,
          });
          // Drop the local blob preview now that the SAS URL is live.
          this.avatarPreview = null;

          this.$store.dispatch("showMessage", {
            message: message || "Avatar updated.",
            messageType: "success",
            timeout: 2000,
          });
        })
        .catch((error) => {
          const message =
            (error.response && error.response.data && error.response.data.message) ||
            "Failed to upload avatar.";
          this.$store.dispatch("showMessage", {
            message,
            messageType: "error",
            timeout: 3000,
          });
          this.avatarPreview = null;
        })
        .finally(() => {
          this.avatarUploading = false;
        });
    },
    resetPasswordForm() {
      this.passwordForm = {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      };
      this.pwErrors = {
        currentPassword: false,
        newPassword: false,
        confirmPassword: false,
      };
    },
    savePassword() {
      // Clear previous errors
      this.pwErrors = { currentPassword: false, newPassword: false, confirmPassword: false };

      // Validate fields and mark empty ones
      let hasEmpty = false;
      if (!this.passwordForm.currentPassword) { this.pwErrors.currentPassword = true; hasEmpty = true; }
      if (!this.passwordForm.newPassword) { this.pwErrors.newPassword = true; hasEmpty = true; }
      if (!this.passwordForm.confirmPassword) { this.pwErrors.confirmPassword = true; hasEmpty = true; }

      if (hasEmpty) {
        this.$store.dispatch("showMessage", {
          message: "Please fill in all password fields.",
          messageType: "error",
          timeout: 2000,
        });
        return;
      }

      if (this.passwordForm.newPassword !== this.passwordForm.confirmPassword) {
        this.$store.dispatch("showMessage", {
          message: "New password and confirmation do not match.",
          messageType: "error",
          timeout: 2000,
        });
        return;
      }

      this.$axios
        .post(
          `${this.$service.sso}/v1/en/auth/password/update`,
          {
            currentPassword: this.passwordForm.currentPassword,
            newPassword: this.passwordForm.newPassword,
            confirmNewPassword: this.passwordForm.confirmPassword,
          },
          {
            headers: {
              Authorization: `Bearer ${this.auth.token}`,
            },
          }
        )
        .then((response) => {
          const { status, message } = response.data;
          if (status) {
            this.$store.dispatch("showMessage", {
              message: message || "Password updated successfully!",
              messageType: "success",
              timeout: 2000,
            });
            this.resetPasswordForm();
          } else {
            this.$store.dispatch("showMessage", {
              message: message || "Failed to update password.",
              messageType: "error",
              timeout: 3000,
            });
          }
        })
        .catch((error) => {
          const message = error.response?.data?.message || "Failed to update password.";
          this.$store.dispatch("showMessage", {
            message,
            messageType: "error",
            timeout: 3000,
          });
        });
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .profile-page {
  background: var(--app-page);
}

.dark-mode .avatar-card {
  background: var(--app-card);
}

.dark-mode .avatar-upload__circle {
  background: linear-gradient(135deg, var(--app-accent-soft), var(--app-accent-soft));
  border: 4px solid var(--app-card);
}

.dark-mode .avatar-upload__overlay {
  border: 2px solid var(--app-card);
}

.dark-mode .avatar-card__name {
  color: var(--app-text);
}

.dark-mode .avatar-card__email {
  color: var(--app-text-faint);
}

.dark-mode .role-chip {
  background: var(--app-accent-soft);
}

.dark-mode .section-card {
  background: var(--app-card);
}

.dark-mode .section-card__header {
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .section-card__icon {
  background: var(--app-accent-soft);
}

.dark-mode .section-card__icon--amber {
  background: var(--app-warning-bg);
}

.dark-mode .section-card__title {
  color: var(--app-text);
}

.dark-mode .section-card__actions {
  border-top: 1px solid var(--app-border);
}

.dark-mode .info-row__label {
  color: var(--app-neutral-fg);
}

.dark-mode .info-input {
  border: 1.5px solid var(--app-border-strong);
  color: var(--app-text);
  background: var(--app-card);
}

.dark-mode .info-input--disabled {
  background: var(--app-surface);
  border: 1.5px dashed var(--app-border);
  color: var(--app-text-faint);
}

.dark-mode .info-row__lock {
  color: var(--app-text-faint);
}

.dark-mode .info-row__hint {
  color: var(--app-text-faint);
}

.dark-mode .btn--outline {
  background: var(--app-card);
  color: var(--app-text-muted);
  border: 1.5px solid var(--app-border-strong);
}

.dark-mode .btn--outline:hover {
  background: var(--app-surface);
  border-color: var(--app-border-strong);
}

/* >>> end auto-dark <<< */

/* ===== PAGE ===== */
.profile-page {
  background: #d8dce2;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
  padding: 32px !important;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  zoom: 1.1;
}

.profile-layout {
  max-width: 960px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 24px;
  align-items: start;
}

/* ===== AVATAR CARD ===== */
.avatar-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  border: 1px solid #e4e8ec;
}

.avatar-card__bg {
  height: 80px;
  background: linear-gradient(135deg, #2c4a7c, #3a6a9e, #5ab8e0);
  position: relative;
}

.avatar-card__bg::after {
  content: '';
  position: absolute;
  inset: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.avatar-card__body {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 24px 28px;
  margin-top: -40px;
  position: relative;
}

/* ===== AVATAR UPLOAD ===== */
.avatar-upload {
  position: relative;
  cursor: pointer;
  margin-bottom: 14px;
}

.avatar-upload--uploading {
  cursor: wait;
  opacity: 0.85;
}

.avatar-upload__circle {
  width: 84px;
  height: 84px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e0f2fe, #bae6fd);
  border: 4px solid #fff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  transition: transform 0.2s ease;
}

.avatar-upload:hover .avatar-upload__circle {
  transform: scale(1.04);
}

.avatar-upload__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-upload__initials {
  font-size: 1.5rem;
  font-weight: 800;
  color: #0ea5e9;
  letter-spacing: 1px;
}

.avatar-upload__overlay {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #0ea5e9;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  transition: background 0.15s;
}

.avatar-upload:hover .avatar-upload__overlay {
  background: #0284c7;
}

.avatar-upload__hidden {
  display: none;
}

.avatar-card__name {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 2px;
  text-align: center;
}

.avatar-card__email {
  font-size: 0.78rem;
  color: #94a3b8;
  margin: 0 0 14px;
  text-align: center;
}

.avatar-card__roles {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
}

.role-chip {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 0.68rem;
  font-weight: 600;
  background: #f0f9ff;
  color: #0369a1;
  border: 1px solid #e0f2fe;
  letter-spacing: 0.2px;
}

/* ===== SECTION CARD ===== */
.details-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.section-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  border: 1px solid #e4e8ec;
  overflow: hidden;
}

.section-card__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 24px;
  border-bottom: 1px solid #f1f5f9;
}

.section-card__icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: #e0f2fe;
  color: #0ea5e9;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.section-card__icon--amber {
  background: #fef3c7;
  color: #d97706;
}

.section-card__title {
  font-size: 0.92rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.section-card__body {
  padding: 20px 24px 24px;
}

.section-card__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
  padding-top: 18px;
  border-top: 1px solid #f1f5f9;
}

/* ===== INFO ROWS ===== */
.info-row {
  margin-bottom: 18px;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-row__label {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 6px;
}

.info-row__field {
  position: relative;
}

.info-input {
  width: 100%;
  padding: 10px 40px 10px 14px;
  border: 1.5px solid #d1d5db;
  border-radius: 10px;
  font-size: 0.85rem;
  font-family: 'Lato', sans-serif;
  font-weight: 500;
  color: #1e293b;
  background: #fff;
  transition: all 0.15s ease;
  outline: none;
}

.info-input::placeholder {
  color: #b0b8c1;
  font-weight: 400;
}

.info-input:focus {
  border-color: #60b8e0;
  box-shadow: 0 0 0 3px rgba(96, 184, 224, 0.1);
}

.info-input--error {
  border-color: #ef4444;
}

.info-input--error:focus {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.info-input--disabled {
  background: #f8fafc;
  border: 1.5px dashed #e2e8f0;
  color: #94a3b8;
  cursor: not-allowed;
}

.info-row__lock {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #cbd5e1;
  display: flex;
}

.info-row__toggle {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
}

.info-row__hint {
  display: block;
  font-size: 0.7rem;
  color: #94a3b8;
  margin-top: 4px;
}

.pw-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

/* ===== BUTTONS ===== */
.btn {
  padding: 9px 20px;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  font-family: 'Lato', sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn--outline {
  background: #fff;
  color: #64748b;
  border: 1.5px solid #d1d5db;
}

.btn--outline:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.btn--primary {
  background: linear-gradient(135deg, #5ab8e0, #60c4ec);
  color: #fff;
  border: none;
}

.btn--primary:hover {
  background: linear-gradient(135deg, #4aa8d0, #55b8e2);
  box-shadow: 0 4px 14px rgba(90, 184, 224, 0.25);
}

/* ===== DARK MODE ===== */
.dark-mode .profile-page {
  background: var(--app-page);
}

.dark-mode .avatar-card {
  background: var(--app-page);
  border-color: var(--app-border);
}

.dark-mode .avatar-card__bg {
  background: linear-gradient(135deg, var(--app-accent-soft), var(--app-accent-soft), var(--app-accent));
}

.dark-mode .avatar-card__name {
  color: var(--app-text);
}

.dark-mode .avatar-card__email {
  color: var(--app-text-faint);
}

.dark-mode .avatar-upload__circle {
  background: linear-gradient(135deg, var(--app-accent-soft), var(--app-accent-soft));
  border-color: var(--app-page);
}

.dark-mode .avatar-upload__initials {
  color: var(--app-accent);
}

.dark-mode .avatar-upload__overlay {
  border-color: var(--app-page);
}

.dark-mode .role-chip {
  background: var(--app-accent-soft);
  color: var(--app-accent);
  border-color: var(--app-accent-border);
}

.dark-mode .section-card {
  background: var(--app-page);
  border-color: var(--app-border);
}

.dark-mode .section-card__header {
  border-bottom-color: var(--app-border);
}

.dark-mode .section-card__icon {
  background: var(--app-accent-soft);
  color: var(--app-accent);
}

.dark-mode .section-card__icon--amber {
  background: var(--app-warning-bg);
  color: var(--app-warning-fg);
}

.dark-mode .section-card__title {
  color: var(--app-text);
}

.dark-mode .section-card__actions {
  border-top-color: var(--app-border);
}

.dark-mode .info-row__label {
  color: var(--app-text-muted);
}

.dark-mode .info-input {
  background: var(--app-page);
  border-color: var(--app-border);
  color: var(--app-text);
}

.dark-mode .info-input::placeholder {
  color: var(--app-text-faint);
}

.dark-mode .info-input:focus {
  border-color: var(--app-accent);
  box-shadow: 0 0 0 3px var(--app-ring);
}

.dark-mode .info-input--disabled {
  background: var(--app-page);
  border-color: var(--app-border);
  color: var(--app-text-faint);
}

.dark-mode .info-row__lock {
  color: var(--app-text-faint);
}

.dark-mode .info-row__hint {
  color: var(--app-text-faint);
}

.dark-mode .btn--outline {
  background: var(--app-page);
  color: var(--app-text-muted);
  border-color: var(--app-border);
}

.dark-mode .btn--outline:hover {
  background: var(--app-card);
  border-color: var(--app-border-strong);
}

/* ===== MOBILE ===== */
@media (max-width: 768px) {
  .profile-page {
    padding: 16px !important;
    zoom: 1;
  }

  .profile-layout {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .avatar-card__body {
    padding: 0 20px 24px;
  }

  .section-card__body {
    padding: 16px 20px 20px;
  }

  .section-card__header {
    padding: 14px 20px;
  }

  .pw-row {
    grid-template-columns: 1fr;
    gap: 0;
  }
}

/* Tablet */
@media (min-width: 769px) and (max-width: 1024px) {
  .profile-page {
    zoom: 1;
  }

  .profile-layout {
    grid-template-columns: 240px 1fr;
    gap: 18px;
  }
}
</style>
